import { TrashIcon } from "@radix-ui/react-icons";
import { Callout, Card, Flex, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Lists } from "../../components/common/Lists";
import TableComponent from "../../components/common/Tables";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetCoursePlansQuery } from "../../hooks/useGetCoursePlansQuery";
import { useGetCurrentAcademicYearQuery } from "../../hooks/useGetCurrentAcademicYearQuery";
import { useGetSubjectsPlansQuery } from "../../hooks/useGetSubjectsQuery";
import { useGetTeacheIdByUserId } from "../../hooks/useGetTeacheIdByUserId";
import { resetSubjectPlanData, setSubjectPlanData } from "../../redux/slices/school/subjectPlan";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { getTeacherClasses, getTeacherSubjects } from "../../services/school/teacher/getTeachers";
import { SubjectPlanType } from "../../types/SubjectPlan";
const columnNamesMap: { [key: string]: string } = {
  subject: 'Disciplina',
  objectives: 'Objectivo',
  subjectReferences: 'Referência',
  assessmentCriteria: 'Critérios de avaliação',
};
export const GetSubjectPlans = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useAppDispatch();

  const { UserId } = useSelector((state: RootState) => state.login.userData)
  const { data: teacherData, isLoading: isLoadingTeacherData } = useGetTeacheIdByUserId(UserId);
  const { data: currentAcademicYear, isLoading: isLoadingCurrentAcademicYear, isError: errorCurrentAcademicYear } = useGetCurrentAcademicYearQuery();

  const { control, handleSubmit, setValue, getValues, formState: { errors }, watch } = useForm({
    defaultValues: { idClass: '', idSubject: "", idCourse: "", idPlan: "" },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });
  const watchInputs = watch();
  const { isLoading: isLoadingClasses, refetch: fetchTeacherClasses, data: classes, isError: isErrorTeacherClasses } = useQuery(
    ['fetchTeacherClasses'],
    async () => {
      try {
        const classes = await getTeacherClasses({ PageNumber: 1, PageSize: 400, IdTeacher: teacherData?.idTeacher });
        return classes;
      } catch (error) {
      }
    },
    {
      enabled: !!teacherData?.idTeacher,
    }
  );
  const { isLoading: isLoadingSubjects, refetch: fetchTeacherSubjects, data: subjects, isError: isErrorTeacherSubjects } = useQuery(
    ['fetchTeacherSubjects'],
    async () => {
      try {
        const subjects = await getTeacherSubjects({ PageNumber: 1, PageSize: 400, IdTeacher: teacherData?.idTeacher, IdClass: getValues('idClass') });
        return subjects;
      } catch (error) {
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );
  const { data: coursePlans, isLoading: isLoadingCoursePlans, isError: errorCoursePlans, refetch: refreshCoursePlans } = useGetCoursePlansQuery(
    { PageNumber: currentPage, PageSize: 400, IdCourse: getValues('idCourse') },
    { enabled: !!getValues('idCourse') });

  const { data: subjectPlansData, status: StatusSubjectPlans, refetch: refreshSubjectPlansData } = useGetSubjectsPlansQuery({
    PageSize: 400,
    PageNumber: 1,
    IdSubject: getValues('idSubject'),
    // IdCourse:  getValues('idCourse'),
    IdPlan: getValues('idPlan')
  },
    {
      enabled: !!watchInputs,//!!getValues('idSubject') && !!getValues('idClass') && !!getValues('idCourse') && !!getValues('idPlan')
    }
  );

  const handleBack = () => {
    navigate(routes.dashboard)
    dispatch(resetSubjectPlanData())
  }
  const handleChangeClass = (idClass: string) => {
    setValue('idClass', idClass);
    const idCourse = classes?.items.find(i => i.id === idClass)?.idCourse!;
    setValue('idCourse', idCourse);
    fetchTeacherSubjects();
  };
  const handleChangeSubject = (idSubject: string) => {
    setValue('idSubject', idSubject);
    refreshSubjectPlansData();
  };

  if (!teacherData || !teacherData.idTeacher) {
    return (
      <Flex direction="column">
        <Callout.Root color="red" role="alert">
          <Text>
            Entre como docente &nbsp;
            <Link to={routes.dashboard} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
          </Text>
        </Callout.Root>

      </Flex>
    );
  }

  const onSubmit = handleSubmit(async (data) => {

  });
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  const manageLessonPlan = (subjectPlan: SubjectPlanType) => {
    subjectPlan.idClass = getValues('idClass')
    subjectPlan.idTeacher = teacherData.idTeacher;
    dispatch(setSubjectPlanData(subjectPlan))
    navigate(routes.manageLessonPlans)
  };
  const options = subjectPlansData?.items.map((ss) => [
    {
      label: 'Gerir plano de aula',
      onClick: () => manageLessonPlan(ss),
      icon: <TrashIcon />
    }

  ]) || [];
  return (
    <Card>
      <Flex direction='column' gap='4'>
        <form onSubmit={onSubmit}>
          <Flex gap='3' direction='column'>
            <Card size='1'>
              <Flex direction='column' gap="2">
                <Flex gap='3'>
                  <SelectComponent name="idClass" label="Turma" control={control} errors={errors} style={{ width: '100%' }}
                    options={classes?.items.map(_class => {
                      return {
                        label: _class.description,
                        value: _class.id ?? ""
                      }
                    })}
                    onSelect={handleChangeClass}
                    itemsLoading={isLoadingClasses}
                    itemsError={isErrorTeacherClasses}
                  />
                  <SelectComponent name='idPlan' label='Plano de curso' control={control} errors={errors} style={{ width: '100%' }}
                    options={coursePlans?.items.map(coursePlan => ({
                      value: coursePlan.id ?? '',
                      label: `${coursePlan.course} - ${coursePlan.academicYear}`
                    }))}
                    required={true}
                  />

                  <SelectComponent name="idSubject" label="Disciplina" control={control} errors={errors} style={{ width: '100%' }}
                    options={subjects?.items.map(subject => {
                      return {
                        label: subject.description,
                        value: subject.id ?? ""
                      }
                    })}
                    itemsLoading={isLoadingSubjects}
                    itemsError={isErrorTeacherSubjects}
                    onSelect={handleChangeSubject}
                  />
                </Flex>
              </Flex>
            </Card>
          </Flex>
        </form>
        {getValues('idSubject') && (
          <Lists
            status={StatusSubjectPlans}
            TableComponent={() => <TableComponent
              columns={Object.keys(columnNamesMap)}
              items={subjectPlansData?.items}
              options={options}
              columnNamesMap={columnNamesMap}
              totalPages={subjectPlansData?.totalPages || 1}
              currentPage={currentPage}
              onPageChange={handlePageChange} />} handleClickCreate={undefined}
          />
        )}

      </Flex>
    </Card>
  )
}